import { AUTO_LOOKUP_BLOCK, INSURANCE_FIND_BLOCKS } from '../constants';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { CustomersController } from '@/shared/endpoints/customers';
import { Box, Button } from '@/shared/ui';
import { useTheme } from '@mui/material/styles';
import { selectInsuranceLookupTriggered, selectOnboardingUser } from '@/store/slices/onboarding/selectors';
import { setInsuranceLookupTriggered } from '@/store/slices/onboarding';
import { withScrollRestoration } from '@/shared/hocs/withScrollRestoration';
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import { GtmEvent } from '@/shared/types/enums';
import { useEffect, useState } from 'react';
import { setPageType } from '@/shared/helpers/page-type';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/shared/constants/routes';
import { checkEligibilityAndSave } from '@/shared/helpers/eligibility';
import { ICustomersInsuranceManualResponse } from '@/shared/endpoints/customers';
import { setEligibilityFailedModalOpen } from '@/store/slices/onboarding';

interface IProps {
    onLookup(): void;
    onContinue(): void;
}

const InsuranceEmptyState = ({ onLookup, onContinue }: IProps) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const sendDataToGTM = useGTMDispatch();
    const navigate = useNavigate();
    const user = useAppSelector(selectOnboardingUser);
    const lookupTriggered = useAppSelector(selectInsuranceLookupTriggered);
    const showLookup = !lookupTriggered && !user?.userProfile?.lastEligibilityRun;
    const [isLoading, setIsLoading] = useState(false);
    const [insuranceLookupYieldedNothing, setInsuranceLookupYieldedNothing] = useState(false);

    const lookUp = async () => {
        setIsLoading(true);
        await dispatch(CustomersController.eligibility()).then(async (res) => {
            // TZ lookup here
            //  insuranceToCheck && await checkEligibilityAndSave({ insuranceList: [...user.insurances, insuranceToCheck] });

            const count = ((res?.payload || []) as unknown[]).length;
            if (count) {
                checkEligibilityAndSave({ insuranceList: res?.payload as ICustomersInsuranceManualResponse[] }).then(eligible => {
                    eligible === false && dispatch(setEligibilityFailedModalOpen(true));
                });
            }
            sendDataToGTM({
                event: GtmEvent.INSURANCE_LOOKUP_COMPLETED,
                userId: user?.userProfile?.accountId,
                count
            });
            if (count === 0) {
                setInsuranceLookupYieldedNothing(true);
            }
        });
        dispatch(setInsuranceLookupTriggered(true));
        onLookup();
        setIsLoading(false);
    };

    const _conv_page_type = showLookup ? 'show_lookup' : 'choose_type_of_adding_insurance';

    const handleNavigate = (path: keyof typeof ROUTES.ONBOARDING.INSURANCE) => {
        navigate(`/${ROUTES.ONBOARDING.MAIN}/${ROUTES.ONBOARDING.INSURANCE.MAIN}/${ROUTES.ONBOARDING.INSURANCE[path]}`);
    };

    useEffect(() => {
        setPageType(_conv_page_type);
    }, [_conv_page_type]);

    return (
        <div
            className="insurance-step-container empty-state flex w-full flex-col flex-wrap items-center"
            data-testid="insurance-empty-state"
        >
            {insuranceLookupYieldedNothing ? (
                <div data-testid="lookup-view" className="insurance-step-container empty-state flex w-full flex-col flex-wrap items-center">
                    <h2 className="mb-[1.8rem] text-center font-agrandir text-[45px] font-normal sm-max:text-2xl">
                        Looks like we couldn’t locate your
                        <br /> insurance details.
                    </h2>

                    <h3 className="px-[1.75rem] text-center font-agrandir text-[2rem] sm-max:text-base">
                        Please select an alternative way to add your insurance
                    </h3>
                </div>
            ) : (
                <>
                    <h2
                        data-testid="easly-view"
                        className="insurance__heading text-center font-agrandir text-[45px] font-normal sm-max:text-2xl"
                    >
                        <span className="text-primary">Easily </span>
                        Add Your Insurance!
                    </h2>

                    {showLookup && (
                        <Box
                            data-testid="insurance-finder"
                            bgWhite
                            shadow
                            sx={{
                                borderRadius: '19px',
                                width: 546,
                                maxWidth: '100%',
                                marginTop: '1.8rem',
                                [theme.breakpoints.up('lg')]: { padding: '30px 35px' }
                            }}
                        >
                            <div className="flex w-full flex-col items-center" data-testid="insurance-option-container">
                                {AUTO_LOOKUP_BLOCK.icon}
                                <h3 className="mt-3 text-center font-agrandir text-21 text-2xl font-semibold sm-max:text-xl">
                                    {AUTO_LOOKUP_BLOCK.name}
                                </h3>
                                <p className="mt-3 text-center text-base">{AUTO_LOOKUP_BLOCK.description}</p>

                                <Button
                                    className="insurance__lookup_btn"
                                    sx={{
                                        width: '100%',
                                        marginTop: '1.5rem',
                                        fontSize: '1.125rem',
                                        [theme.breakpoints.down('sm')]: { fontSize: '1rem' }
                                    }}
                                    showEndIcon
                                    onClick={lookUp}
                                    loading={isLoading}
                                    testId="button-insurance-empty-state-lookup"
                                >
                                    Look Up My Insurance
                                </Button>
                            </div>
                        </Box>
                    )}

                    <h3 className="mt-[2.8rem] px-[1.75rem] text-center font-agrandir text-2xl sm-max:mt-[2rem] sm-max:text-21">
                        Want to add your insurance in a different way?
                    </h3>
                </>
            )}

            <div className="mt-[2rem] flex max-w-[100%] lg-max:flex-col">
                {INSURANCE_FIND_BLOCKS.map(way => (
                    <Box
                        key={way.type}
                        bgWhite
                        shadow
                        sx={{
                            borderRadius: '19px',
                            width: 378,
                            maxWidth: '100%',
                            [theme.breakpoints.up('lg')]: {
                                padding: '30px 35px',
                                '&:not(:last-child)': { marginRight: '1.8rem' }
                            },
                            [theme.breakpoints.down('lg')]: {
                                padding: '20px 26px',
                                '&:not(:last-child)': { marginBottom: '1.5rem' }
                            }
                        }}
                    >
                        <div className="flex w-full flex-col items-center" key={way.type}>
                            {way.icon}
                            <h3 className="font-agrandir text-xl font-semibold">{way.name}</h3>
                            <p className="mt-3 text-center text-base">{way.description}</p>
                            {/* @ts-ignore-next-line */}
                            <Button
                                disabled={isLoading}
                                className={`insurance__add_${way.type}_btn`}
                                theme="dark"
                                variant="outlined"
                                sx={{ width: '100%', marginTop: '1.5rem', fontSize: '1rem' }}
                                onClick={() => handleNavigate(way.path as keyof typeof ROUTES.ONBOARDING.INSURANCE)}
                                testId={`button-insurance-empty-state-option-${way.type}`}
                            >
                                {way.buttonName}
                            </Button>
                        </div>
                    </Box>
                ))}
            </div>

            <h3 className="mb-[9px] mt-[46px] font-agrandir text-2xl">Do not have insurance?</h3>

            <Button
                disabled={isLoading}
                className="insurance__submit_btn"
                type="submit"
                sx={{ fontSize: '1.125rem' }}
                variant="text"
                onClick={onContinue}
                testId="button-insurance-empty-state-proceed"
            >
                Proceed without insurance
            </Button>
        </div>
    );
};

export default withScrollRestoration(InsuranceEmptyState);
